<template>
  <button
    :class="[
      $blockClass(blockClass, 'wishlist'),
      'wishlist-button',
      {'wishlist-button--active': active}
    ]"
    name="wishlist"
    :aria-label="$string(`accessibility.wishlist.${active ? 'remove' : 'add'}`)"
    type="button"
    @mousedown="loadComponent('gift-reggie');"
    @touchstart="loadComponent('gift-reggie');"
    @click.prevent="openWishlistModal"
    v-html="buttonLabel"
  />
</template>

<script>


/**
 * Vue: Gift reggie button (gift-reggie-button)
 * -----------------------------------------------------------------------------
 * Clicking the heart on PDP or product card will trigger the gift reggie
 * overlay.
 *
 * @param {String} activeLabel - Text to use when item is in wishlist instead
 * of an icon.
 * @param {String} blockClass - Additional block class to apply to the button.
 * @param {String} icon - The name of the icon to use for this button.
 * @param {String} label - Text to use in place of icon.
 * @param {Number} productId - The current product ID. Used for adding to
 * registry.
 * @param {Number} variantId - The current variant ID to be added to wishlist.
 *
 */

import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'

export default {
  name: 'WishlistButton',

  components: {
    GiftReggie: defineAsyncComponent({
      loader: () => import(
        /* webpackPrefetch: true */
        '~async/gift-reggie/gift-reggie'
        ),
    }),
  },

  props: {
    activeLabel: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'enclosed.heartXLarge',
    },
    label: {
      type: String,
      default: '',
    },
    productId: {
      type: Number,
      default: 0,
      required: true,
    },
    variantId: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  data() {
    return {
      changedState: '',
    }
  },

  computed: {
    ...mapState({
      cart: (state) => state.cart.response,
    }),

    active() {
      const { wishlist_variant_ids } = this.cart.attributes

      if (!wishlist_variant_ids || !this.variantId) {
        return false
      }

      const variants = JSON.parse(JSON.stringify(wishlist_variant_ids))
      if (this.changedState === 'removed'){
        return false
      } else if (this.changedState === 'added'){
        return true
      } else {
        return variants.indexOf(this.variantId) > -1
      }
    },

    /**
     * Compute the button label.
     * @returns {string}
     */
    buttonLabel() {
      if (this.active && this.activeLabel) {
        return this.activeLabel
      }

      return this.label || this.$icon(this.icon)
    },
  },

  watch: {
    variantId() {
      this.changedState = ''
    },
    productId() {
      this.changedState = ''
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
    }),

    /**
     * Update the wishlist modal with the selected variant / product and open
     * the modal.
     */
    openWishlistModal() {
      if (!this.productId || !this.variantId) {
        return
      }

      /* Here we're going to emit what's going to happen on the other side */
      /* If we're adding it to the Wishlist then we check on the other side for the session as well */
      this.updateModal()

      if (!this.active) {
        this.changedState = 'added'
        setTimeout(()=>{
          this.openOverlay({
            component: 'gift-reggie',
            ignoreDismissed: true,
            namespace: 'giftReggie',
          })
        }, 600)
      } else {
        this.changedState = 'removed'
      }

    },

    updateModal() {
      cnvs.EventBus.emit('giftReggie:update', {
        variantId: this.variantId,
        productId: this.productId,
        action: this.active ? 'remove' : 'add',
      })
    },
  },
}
</script>
